import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import * as Clipboard from 'expo-clipboard';
import { useRouter } from 'expo-router';
import { useScrollToTop } from 'expo-router/react-navigation';
import { useAtomValue } from 'jotai';
import { X } from 'lucide-react-native';
import { useMemo, useRef, useState } from 'react';
import { Dimensions } from 'react-native';

import { primum_default_avatar } from '@/assets/images';
import { AccountListRow } from '@/components/account/AccountListRow';
import { ActAsAccountRow } from '@/components/account/ActAsAccountRow';
import { AwayScheduleSheet } from '@/components/account/AwayScheduleSheet';
import {
  AWAY_CALENDAR_NEW_LABEL,
  canShowAwayCalendarBadge,
  getAvailabilityRowLabel,
} from '@/components/account/awayCalendarIntro/awayCalendarIntroModel';
import {
  AwayCalendarTourProvider,
  AwayCalendarTourZone,
} from '@/components/account/awayCalendarTour/AwayCalendarTour';
import { LegacyAwaySwitch } from '@/components/account/LegacyAwaySwitch';
import { ImpersonationSearch } from '@/components/account/ImpersonationSearch';
import { PrimumSection } from '@/components/account/PrimumSection';
import { AwayBanner } from '@/components/awayBanner';
import { Header } from '@/components/header';
import { SUPPORT_REASON_AWAY_OVER_30_DAYS, SupportForm } from '@/components/supportForm';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Button, ButtonText } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { FlatList } from '@/components/ui/flat-list';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Link, LinkText } from '@/components/ui/link';
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@/components/ui/modal';
import { Pill } from '@/components/ui/pill';
import { Pressable } from '@/components/ui/pressable';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { UpdateReadyBanner } from '@/components/updateReadyBanner';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ACCOUNT_BANNER_CONTENT } from '@/constants/banners';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { NAV_LABELS } from '@/constants/navigation';
import { ROUTES } from '@/constants/routes';
import { useAwayScheduleQuery } from '@/hooks/useAwaySchedule';
import { useToday } from '@/hooks/useToday';
import { useAccountSwitcher } from '@/hooks/useAccountSwitcher';
import { useSupportPrompt } from '@/features/support-contact';
import { useCognitoGroups } from '@/hooks/useCognitoGroups';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useResumableSurveys } from '@/features/surveys';
import { usePollHistory } from '@/features/polls';
import { useToaster } from '@/hooks/useToaster';
import { useTourCallout } from '@/hooks/useTourCallout';
import { signOut } from '@/services/auth';
import { getCurrentUserV2QueryOptions } from '@/services/getCurrentUserV2';
import { userIdAtom } from '@/services/globalState';
import { useSurveyVisitsStore } from '@/features/surveys/state';
import { CALLOUT_KEYS } from '@/services/userCallouts';
import { segmentClient } from '@/util/analytics';
import { trackPollHistoryViewed } from '@/util/analyticsEvents';
import { env } from '@/util/env';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getBuildInformation, getAppVersion } from '@/util/getBuildInformation';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import { getAvailabilityRowState, getAwayPeriod } from '@/util/awaySchedule';
import { navigateIntoTabs } from '@/util/navigateIntoTabs';
import { formatAnsweredPollCount } from '@/features/polls/model';

const IS_SMALL_SCREEN = Dimensions.get('window').width < 800;

const AWAY_OVER_30_DAYS_DRAFT = "I'd like to be away for more than 30 days. My dates: ";

/**
 * Account Screen
 *
 * This component serves as the main hub for user account management. It displays user profile
 * information (avatar, name), provides navigation to various settings pages, and includes
 * functionality for status updates (for experts), logging out, and a link to Developer settings for staff.
 *
 * Features:
 * - Displays user avatar and name.
 * - Allows experts to set their status to "away".
 * - Provides navigation links to Profile, Contact Details, and Notification Settings.
 * - Includes a "Contact Support" modal.
 * - Shows a "Version Information" modal with build details. A tap or a long press on the
 *   Version text opens it.
 * - Handles optimistic updates for user status changes using React Query.
 */
export default function Account() {
  const { navigate } = useRouter();

  const toaster = useToaster();

  const handleLogout = () => {
    signOut(undefined, 'account');
  };

  const { isPrimumUser } = useCognitoGroups();

  const {
    isEnabled: isAccountSwitcherEnabled,
    canSearch: canSearchAccounts,
    actAsUserId,
    currentAccount,
    supportAccounts,
    switchToAccount,
  } = useAccountSwitcher();

  const isOnboardingSurveyPreviewEnabled = useFeatureFlag(FEATURE_FLAGS.ONBOARDING_SURVEY_PREVIEW);

  const onboardingSurveyGroupId = env.EXPO_PUBLIC_ONBOARDING_SURVEY_GROUP_ID;

  const showOnboardingSurveyPreview = isOnboardingSurveyPreviewEnabled && !!onboardingSurveyGroupId;

  const { pending: resumableSurveys } = useResumableSurveys();

  const { polls: answeredPolls } = usePollHistory();

  const acknowledgeSurvey = useSurveyVisitsStore((state) => state.acknowledge);

  const [isOpen, setIsOpen] = useState(false);

  const supportPrompt = useSupportPrompt({ source: 'account_menu' }, { overTabs: true });

  /**
   * Opens the member's support thread with the message already started, or the
   * form filled in the same way when they cannot have a thread. The sheet closes first.
   */
  const awaySupportPrompt = useSupportPrompt(
    { source: 'away_calendar', draft: AWAY_OVER_30_DAYS_DRAFT },
    { overTabs: true }
  );

  const userId = useAtomValue(userIdAtom);

  const { data, isLoading } = useQuery({
    ...getCurrentUserV2QueryOptions(userId),
    placeholderData: keepPreviousData,
  });

  const isExpert = data?.userType === 'expert';

  const today = useToday();

  const isAwayCalendarEnabled = useFeatureFlag(FEATURE_FLAGS.EXPERT_AWAY_CALENDAR);

  const showAwayCalendar = isExpert && isAwayCalendarEnabled;

  const { data: awayBlocks = [] } = useAwayScheduleQuery({ enabled: showAwayCalendar });

  const awayPeriod = getAwayPeriod(awayBlocks, today);

  // The old Active/Away switch set an Away status with no end date. The switch is
  // gone, but a status already set still counts until the expert ends it.
  const legacyAway = data?.status === '2';

  const availability = getAvailabilityRowState({
    period: awayPeriod,
    today,
    legacyAway,
  });

  const [isAwaySheetOpen, setIsAwaySheetOpen] = useState(false);

  /**
   * The New badge stays until the expert first opens the date sheet, so it
   * outlasts a tour dismissed without trying the feature. Its own callout, so
   * the tour does not use it up; the key keeps its old name as a contract.
   */
  const newBadge = useTourCallout({
    key: CALLOUT_KEYS.EXPERT_AWAY_CALENDAR_SHEET_HINT,
    enabled: showAwayCalendar && !isLoading,
  });

  const isAwayNew = canShowAwayCalendarBadge({
    isEligible: showAwayCalendar && !isLoading,
    shouldShowCallout: newBadge.shouldShow,
  });

  const { name, nameWithoutDoctor } = getProfileNameAndInitials({
    firstName: data?.firstName,
    lastName: data?.lastName,
    identifiesAsDoctor: data?.identifiesAsDoctor,
  });

  const settings = useMemo(
    () => [
      ...(showAwayCalendar
        ? [
            {
              title: 'Availability',
              subtitle: availability.subtitle,
              accessibilityLabel: getAvailabilityRowLabel({
                isNew: isAwayNew,
                subtitle: availability.subtitle,
              }),
              hint: 'Opens time away',
              badge:
                availability.isAway || isAwayNew ? (
                  <>
                    {availability.isAway ? <Pill tone="warning">Away</Pill> : null}

                    {isAwayNew ? <Pill tone="info">{AWAY_CALENDAR_NEW_LABEL}</Pill> : null}
                  </>
                ) : undefined,
              onPress: () => {
                if (newBadge.shouldShow) {
                  newBadge.markSeen();
                }

                setIsAwaySheetOpen(true);
              },
              isTourStop: true,
            },
          ]
        : []),
      {
        title: 'Profile details',
        onPress: () => {
          segmentClient.track(ANALYTICS_EVENTS.PROFILE_VIEWED, {
            source: 'account_settings',
          });

          navigateIntoTabs('/profile/me');
        },
      },
      { title: 'Contact details', onPress: () => navigate('/account/contact-details') },
      {
        title: 'Notification settings',
        onPress: () => {
          segmentClient.track(ANALYTICS_EVENTS.MENU_SETTINGS_CLICKED);

          navigate('/account/notification-settings');
        },
      },
      ...(data?.userType === 'expert'
        ? [
            {
              title: 'Ask me on Primum',
              subtitle: 'Share your link or QR code',
              onPress: () => navigate(ROUTES.ACCOUNT_QR_CODE),
            },
          ]
        : []),
      {
        title: NAV_LABELS.invites.mobileTitle,
        onPress: () => {
          segmentClient.track(ANALYTICS_EVENTS.INVITE_A_PEER_CLICKED, {
            source: 'account_screen',
          });

          navigate('/account/invites');
        },
      },
      {
        title: 'Contact support',
        onPress: supportPrompt.open,
      },
      ...(showOnboardingSurveyPreview
        ? [
            {
              title: 'Preview onboarding survey',
              onPress: () => navigate(ROUTES.SURVEY(onboardingSurveyGroupId, { preview: true })),
            },
          ]
        : []),
    ],
    [
      showOnboardingSurveyPreview,
      onboardingSurveyGroupId,
      data?.userType,
      availability.subtitle,
      availability.isAway,
      showAwayCalendar,
      isAwayNew,
      newBadge.shouldShow,
      newBadge.markSeen,
    ]
  );

  const avatarUrl = getAvatarUrl(data?.avatar, true);

  const flatListRef = useRef<FlatList>(null);

  const versionDetails = getBuildInformation().filter((item) => !item.debug);

  const appVersion = useMemo(() => {
    const helperVersion = getAppVersion();

    if (helperVersion) {
      return helperVersion;
    }

    if (typeof window !== 'undefined' && window.__BUILD_INFO__) {
      const v = window.__BUILD_INFO__['version'];

      if (v == null) {
        return '';
      }

      return typeof v === 'string' ? v : String(v);
    }

    return '';
  }, []);

  /**
   * The away and update banners. Web shows them above the list. Native puts them at the top of
   * the list itself, full width, so the list is the first thing under the floating bar and its
   * content can scroll beneath it; a banner above the list would sit under the bar.
   */
  const accountBanners = (
    <>
      <AwayBanner userId={userId} contentClassName={ACCOUNT_BANNER_CONTENT} />

      <UpdateReadyBanner />
    </>
  );

  return (
    <AwayCalendarTourProvider
      isEligible={showAwayCalendar && !isLoading}
      isCovered={
        isOpen || isAwaySheetOpen || supportPrompt.isFormOpen || awaySupportPrompt.isFormOpen
      }
      isStopOpen={isAwaySheetOpen}
      className="mx-auto w-full max-w-screen-lg flex-1">
      <Header
        headerShown={!isWeb}
        title="Account"
        isBackButton
        backRoute={ROUTES.DIRECTORY}
        contentScrollsUnderHeader
      />

      {isWeb ? accountBanners : null}

      <VStack className="w-full flex-1">
        {isLoading ? (
          <Spinner size="large" className="absolute bottom-0 left-0 right-0 top-0" />
        ) : (
          <HStack className="flex-1 gap-x-10">
            <FlatList
              ref={flatListRef}
              className="w-full grow-[2] basis-0"
              contentContainerClassName="mx-auto w-full gap-4 px-5 py-6 md:max-w-2xl md:px-0 flex-2"
              showsVerticalScrollIndicator={false}
              contentInsetAdjustmentBehavior="automatic"
              ListHeaderComponent={
                <VStack className="w-full items-start justify-center">
                  {isWeb ? null : (
                    <View className="-mx-5 mb-4 self-stretch md:mx-0">{accountBanners}</View>
                  )}

                  <HStack className="mb-8 items-center gap-4">
                    <Avatar size="lg">
                      <AvatarImage source={avatarUrl || primum_default_avatar} />
                    </Avatar>

                    <Heading size="h2-xl">{name}</Heading>
                  </HStack>

                  {resumableSurveys.length > 0 || answeredPolls.length > 0 ? (
                    <VStack className="mb-8 w-full gap-4">
                      <Heading size="h3-lg" className="text-primary-500">
                        Activity
                      </Heading>

                      <Divider />

                      {resumableSurveys.map((survey) => (
                        <AccountListRow
                          key={survey.groupId}
                          title={survey.name}
                          badge={<Pill tone="warning">Incomplete</Pill>}
                          onPress={() => {
                            if (userId) {
                              acknowledgeSurvey(userId, survey.groupId).catch(() => {});
                            }

                            navigate(ROUTES.SURVEY(survey.groupId));
                          }}
                        />
                      ))}

                      {answeredPolls.length > 0 ? (
                        <AccountListRow
                          title="Your polls"
                          subtitle={formatAnsweredPollCount(answeredPolls.length)}
                          onPress={() => {
                            trackPollHistoryViewed({ source: 'account_screen' });

                            navigate(ROUTES.ACCOUNT_POLLS);
                          }}
                        />
                      ) : null}
                    </VStack>
                  ) : null}

                  <PrimumSection
                    onOpenDeveloperSettings={
                      isPrimumUser ? () => navigate(ROUTES.PRIMUM_SETTINGS) : undefined
                    }
                    actAs={
                      isAccountSwitcherEnabled ? (
                        <VStack className="w-full">
                          <ActAsAccountRow
                            title="Act as another account"
                            subtitle={
                              canSearchAccounts
                                ? 'Act as another account, or search for any account'
                                : 'Act as another account'
                            }
                            actAsUserId={actAsUserId ?? undefined}
                            currentLabel={
                              actAsUserId ? (currentAccount?.label ?? 'Unknown') : 'Myself'
                            }
                            accounts={supportAccounts}
                            onSelect={switchToAccount}
                          />

                          {canSearchAccounts ? (
                            <View className="pb-4">
                              <ImpersonationSearch onSelect={(userId) => switchToAccount(userId)} />
                            </View>
                          ) : null}
                        </VStack>
                      ) : undefined
                    }
                  />

                  <Heading size="h3-lg" className="mb-4 text-primary-500">
                    Settings
                  </Heading>

                  {isExpert && !isAwayCalendarEnabled ? <LegacyAwaySwitch userId={userId} /> : null}

                  <Divider />
                </VStack>
              }
              data={settings}
              renderItem={({ item }) => (
                <AccountListRow
                  title={item.title}
                  subtitle={item.subtitle}
                  badge={'badge' in item ? item.badge : undefined}
                  accessibilityLabel={
                    'accessibilityLabel' in item ? item.accessibilityLabel : undefined
                  }
                  hint={'hint' in item ? item.hint : undefined}
                  wrapContent={
                    'isTourStop' in item
                      ? (content) => <AwayCalendarTourZone>{content}</AwayCalendarTourZone>
                      : undefined
                  }
                  onPress={item.onPress}
                />
              )}
              ListFooterComponent={() => (
                <VStack className="mt-8">
                  <VStack space="lg" className="mt-2">
                    <Button
                      size={IS_SMALL_SCREEN ? 'sm' : 'md'}
                      onPress={handleLogout}
                      variant="outline"
                      className="self-start">
                      <ButtonText>Sign Out</ButtonText>
                    </Button>

                    <Link onPress={() => navigate('/account/delete-account')} className="mt-12">
                      <LinkText className="text-primary-500 data-[hover=true]:text-primary-600">
                        Delete account
                      </LinkText>
                    </Link>

                    <Pressable
                      className="mt-4"
                      onPress={() => setIsOpen(true)}
                      onLongPress={() => setIsOpen(true)}>
                      <Text className="select-none text-typography-600">
                        Version {appVersion || 'dev'}
                      </Text>
                    </Pressable>
                  </VStack>
                </VStack>
              )}
            />
          </HStack>
        )}

        <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="lg">
          <ModalBackdrop />

          <ModalContent>
            <ModalHeader>
              <Heading size="h1-lg">Version information</Heading>

              <ModalCloseButton
                hitSlop={{ top: 12, right: 12, bottom: 12, left: 12 }}
                accessibilityLabel="Close modal"
                accessibilityRole="button">
                <Icon as={X} size="lg" />
              </ModalCloseButton>
            </ModalHeader>

            <VStack className="py-2">
              <Divider />
            </VStack>

            <ModalBody>
              <VStack space="sm">
                {versionDetails.map((item) => (
                  <Text key={item.key} size="sm" className="text-typography-700">
                    {item.label}:{' '}
                    <Text className="font-mono font-semibold">{item.display ?? item.value}</Text>
                  </Text>
                ))}
              </VStack>
            </ModalBody>

            <ModalFooter>
              <HStack space="md" className="w-full justify-end">
                <Button
                  onPress={async () => {
                    try {
                      await Clipboard.setStringAsync(
                        versionDetails
                          .flatMap(
                            ({ label, value, copyLines }) => copyLines ?? [`${label}: ${value}`]
                          )
                          .join('\n')
                      );

                      toaster.showSuccess({
                        title: 'Version information copied',
                      });
                    } catch (error) {
                      console.error('Account:copy-version-info:error', error);

                      toaster.showError({
                        title: 'Unable to copy version information',
                      });
                    }
                  }}>
                  <ButtonText>Copy</ButtonText>
                </Button>
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
      <SupportForm
        isOpen={supportPrompt.isFormOpen}
        onClose={supportPrompt.closeForm}
        initialValues={{
          name: nameWithoutDoctor,
          email: data?.email ?? undefined,
        }}
        npi={data?.npiNumber ?? undefined}
        institution={data?.institution ?? undefined}
        state={data?.state ?? undefined}
        firstName={data?.firstName ?? undefined}
        lastName={data?.lastName ?? undefined}
        identifiesAsDoctor={data?.identifiesAsDoctor}
      />
      {/* Mounted only while open: the form reads its starting values once, when it mounts. */}
      {awaySupportPrompt.isFormOpen ? (
        <SupportForm
          isOpen
          onClose={awaySupportPrompt.closeForm}
          initialValues={{
            name: nameWithoutDoctor,
            email: data?.email ?? undefined,
            reason: SUPPORT_REASON_AWAY_OVER_30_DAYS,
            message: AWAY_OVER_30_DAYS_DRAFT,
          }}
          npi={data?.npiNumber ?? undefined}
          institution={data?.institution ?? undefined}
          state={data?.state ?? undefined}
          firstName={data?.firstName ?? undefined}
          lastName={data?.lastName ?? undefined}
          identifiesAsDoctor={data?.identifiesAsDoctor}
        />
      ) : null}
      {showAwayCalendar ? (
        <AwayScheduleSheet
          isOpen={isAwaySheetOpen}
          onClose={() => setIsAwaySheetOpen(false)}
          userId={userId}
          today={today}
          period={awayPeriod}
          otherBlocks={awayBlocks.filter((block) => block.id !== awayPeriod?.id)}
          legacyAway={legacyAway}
          onContactSupport={awaySupportPrompt.open}
        />
      ) : null}
    </AwayCalendarTourProvider>
  );
}
